import {
  listUsers
} from "./chunk-O3EQCY62.js";
import {
  clearDeviceUnauthorized,
  getDeviceUnauthorizedSnapshot,
  subscribeDeviceUnauthorized
} from "./chunk-KVCLZREV.js";
import {
  currentDeviceId,
  signedFetch
} from "./chunk-2CSC3FHL.js";

// lib/device/api.ts
import {
  apiUrlEndpointFactory,
  parseJsonResponse
} from "@lune/common/api";
var devicesBase = (version, root) => apiUrlEndpointFactory(version, root, "/auth/devices").endpoint;
var devicesBaseUrlForTesting = devicesBase;
var listDevices = async (version, root) => {
  const res = await signedFetch(devicesBase(version, root));
  const data = await parseJsonResponse(res);
  return data.items;
};
var updateDevice = async (version, root, id, update) => {
  const res = await signedFetch(`${devicesBase(version, root)}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update)
  });
  const data = await parseJsonResponse(res);
  return data.item;
};
var removeDevice = async (version, root, id) => {
  await signedFetch(`${devicesBase(version, root)}/${id}`, { method: "DELETE" });
};

// lib/device/DevicesDialog.tsx
import { timeAgo } from "@lune/common/date";
import {
  BlockOutlined,
  CheckCircleOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  HighlightOffOutlined,
  PendingOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var deviceKey = (device) => `${device.userId}:${device.id}`;
var STALE_DEVICE_DAYS = 30;
var daysSince = (date, now = /* @__PURE__ */ new Date()) => Math.floor((now.getTime() - date.getTime()) / (1e3 * 60 * 60 * 24));
var isStale = (device) => device.lastSeenAt == null || daysSince(new Date(device.lastSeenAt)) >= STALE_DEVICE_DAYS;
var sortByLastSeenDesc = (devices) => [...devices].sort((a, b) => {
  const aTime = a.lastSeenAt != null ? new Date(a.lastSeenAt).getTime() : 0;
  const bTime = b.lastSeenAt != null ? new Date(b.lastSeenAt).getTime() : 0;
  return bTime - aTime;
});
var formatActivity = (device) => {
  if (device.lastSeenAt == null) return "Never used";
  const when = timeAgo(new Date(device.lastSeenAt));
  const from = device.lastSeenIp != null && device.lastSeenIp !== "" ? ` from ${device.lastSeenIp}` : "";
  return `Last access ${when}${from}`;
};
var formatDeviceInfo = (device) => {
  const parts = [device.browser, device.os].filter(
    (part) => part != null && part !== ""
  );
  return parts.length > 0 ? parts.join(" \xB7 ") : null;
};
var groupByUser = (devices) => {
  const byUser = /* @__PURE__ */ new Map();
  for (const device of devices) {
    const existing = byUser.get(device.userId);
    if (existing != null) {
      existing.push(device);
    } else {
      byUser.set(device.userId, [device]);
    }
  }
  return [...byUser.entries()].sort(([a], [b]) => a.localeCompare(b));
};
var statusChipProps = (status) => {
  switch (status) {
    case "approved":
      return {
        label: "Approved",
        color: "success",
        icon: /* @__PURE__ */ jsx(CheckCircleOutlined, {})
      };
    case "rejected":
      return {
        label: "Rejected",
        color: "error",
        icon: /* @__PURE__ */ jsx(HighlightOffOutlined, {})
      };
    case "disabled":
      return { label: "Disabled", color: "default", icon: /* @__PURE__ */ jsx(BlockOutlined, {}) };
    case "pending":
    default:
      return {
        label: "Pending approval",
        color: "warning",
        icon: /* @__PURE__ */ jsx(PendingOutlined, {})
      };
  }
};
var DevicesDialog = ({
  open,
  onClose,
  version,
  rootUrl,
  isAdmin
}) => {
  const [devices, setDevices] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busyKey, setBusyKey] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [edit, setEdit] = React.useState(null);
  const [thisDeviceId, setThisDeviceId] = React.useState(null);
  React.useEffect(() => {
    if (!open) return;
    currentDeviceId().then(setThisDeviceId).catch(() => {
    });
  }, [open]);
  const reload = React.useCallback(() => {
    setLoading(true);
    setErrorMessage(null);
    Promise.all([
      listDevices(version, rootUrl),
      isAdmin ? listUsers(version, rootUrl) : Promise.resolve([])
    ]).then(([deviceItems, userItems]) => {
      setDevices(deviceItems);
      setUsers(userItems);
    }).catch(() => {
      setErrorMessage("Failed to load devices.");
    }).finally(() => setLoading(false));
  }, [version, rootUrl, isAdmin]);
  React.useEffect(() => {
    if (open) reload();
    if (!open) setEdit(null);
  }, [open, reload]);
  const closeEdit = () => setEdit(null);
  const startRename = (device) => {
    setEdit({
      key: deviceKey(device),
      id: device.id,
      userId: device.userId,
      mode: "rename",
      name: device.name,
      description: device.description ?? ""
    });
  };
  const startApprove = (device) => {
    setEdit({
      key: deviceKey(device),
      id: device.id,
      userId: device.userId,
      mode: "approve",
      name: device.name,
      description: device.description ?? ""
    });
  };
  const handleSaveEdit = async () => {
    if (edit == null || edit.name.trim() === "") return;
    setBusyKey(edit.key);
    try {
      await updateDevice(version, rootUrl, edit.id, {
        userId: edit.userId,
        ...edit.mode === "approve" ? { status: "approved" } : {},
        name: edit.name,
        description: edit.description
      });
      setEdit(null);
      reload();
    } catch {
      setErrorMessage("Failed to save device.");
    } finally {
      setBusyKey(null);
    }
  };
  const handleStatusChange = async (device, status) => {
    setBusyKey(deviceKey(device));
    try {
      await updateDevice(version, rootUrl, device.id, {
        userId: device.userId,
        status
      });
      reload();
    } catch {
      setErrorMessage(`Failed to update "${device.name || device.id}".`);
    } finally {
      setBusyKey(null);
    }
  };
  const handleRemove = async (device) => {
    setBusyKey(deviceKey(device));
    try {
      await removeDevice(version, rootUrl, device.id);
      reload();
    } catch {
      setErrorMessage(`Failed to remove "${device.name || device.id}".`);
    } finally {
      setBusyKey(null);
    }
  };
  const renderActions = (device) => {
    const busy = busyKey === deviceKey(device);
    switch (device.status) {
      case "pending":
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "small",
              variant: "contained",
              disabled: busy,
              onClick: () => startApprove(device),
              children: "Approve\u2026"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "small",
              color: "error",
              disabled: busy,
              onClick: () => void handleStatusChange(device, "rejected"),
              children: "Reject"
            }
          )
        ] });
      case "approved":
        return /* @__PURE__ */ jsx(
          Button,
          {
            size: "small",
            color: "warning",
            disabled: busy,
            onClick: () => void handleStatusChange(device, "disabled"),
            children: "Disable"
          }
        );
      case "rejected":
      case "disabled":
        return /* @__PURE__ */ jsx(
          Button,
          {
            size: "small",
            variant: "contained",
            disabled: busy,
            onClick: () => startApprove(device),
            children: "Approve\u2026"
          }
        );
      default:
        return null;
    }
  };
  const renderDevice = (device) => {
    const chip = statusChipProps(device.status);
    const busy = busyKey === deviceKey(device);
    return /* @__PURE__ */ jsxs(
      Paper,
      {
        variant: "outlined",
        sx: {
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          p: 1.5
        },
        children: [
          /* @__PURE__ */ jsxs(Box, { sx: { flexGrow: 1, minWidth: 240 }, children: [
            /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [
              /* @__PURE__ */ jsxs(
                Typography,
                {
                  variant: "body2",
                  color: device.name ? "text.primary" : "text.disabled",
                  children: [
                    device.name || "Unnamed device",
                    device.id === thisDeviceId && /* @__PURE__ */ jsxs(
                      Typography,
                      {
                        component: "span",
                        variant: "body2",
                        color: "text.secondary",
                        children: [
                          " ",
                          "(this device)"
                        ]
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsx(
                IconButton,
                {
                  size: "small",
                  "aria-label": `rename ${device.name || device.id}`,
                  disabled: busy,
                  onClick: () => startRename(device),
                  children: /* @__PURE__ */ jsx(EditOutlined, { fontSize: "inherit" })
                }
              )
            ] }),
            device.description != null && device.description !== "" && /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "body2",
                color: "text.secondary",
                sx: { display: "block", whiteSpace: "pre-wrap" },
                children: device.description
              }
            ),
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "caption",
                color: "text.secondary",
                sx: { display: "block", fontFamily: "monospace" },
                children: device.id
              }
            ),
            /* @__PURE__ */ jsx(
              Tooltip,
              {
                title: device.lastSeenAt != null ? new Date(device.lastSeenAt).toLocaleString() : "",
                children: /* @__PURE__ */ jsxs(
                  Typography,
                  {
                    variant: "caption",
                    color: isStale(device) ? "warning.main" : "text.secondary",
                    sx: { display: "flex", alignItems: "center", gap: 0.5 },
                    children: [
                      isStale(device) && device.lastSeenAt != null && /* @__PURE__ */ jsx(WarningAmberOutlined, { sx: { fontSize: 14 } }),
                      isStale(device) && device.lastSeenAt != null ? `${daysSince(new Date(device.lastSeenAt))} days since last access` : formatActivity(device)
                    ]
                  }
                )
              }
            ),
            formatDeviceInfo(device) != null && /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "caption",
                color: "text.secondary",
                sx: { display: "block" },
                children: formatDeviceInfo(device)
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            Chip,
            {
              size: "small",
              icon: chip.icon,
              color: chip.color,
              label: chip.label,
              sx: { mr: 2 }
            }
          ),
          renderActions(device),
          /* @__PURE__ */ jsx(
            IconButton,
            {
              edge: "end",
              color: "error",
              disabled: busy,
              onClick: () => void handleRemove(device),
              children: /* @__PURE__ */ jsx(DeleteOutlineOutlined, { fontSize: "small" })
            }
          )
        ]
      },
      deviceKey(device)
    );
  };
  const renderDeviceList = (deviceList) => {
    const sorted = sortByLastSeenDesc(deviceList);
    const recent = sorted.filter((device) => !isStale(device));
    const stale = sorted.filter((device) => isStale(device));
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Stack, { spacing: 1, children: recent.map(renderDevice) }),
      stale.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(
          Typography,
          {
            variant: "overline",
            color: "warning.main",
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: recent.length > 0 ? 2 : 0,
              mb: 1
            },
            children: [
              /* @__PURE__ */ jsx(WarningAmberOutlined, { fontSize: "small" }),
              "Not accessed in over ",
              STALE_DEVICE_DAYS,
              " days"
            ]
          }
        ),
        /* @__PURE__ */ jsx(Stack, { spacing: 1, children: stale.map(renderDevice) })
      ] })
    ] });
  };
  const userHeading = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user != null ? `${user.display} (${userId})` : userId;
  };
  const editBusy = edit != null && busyKey === edit.key;
  const editNameMissing = edit != null && edit.name.trim() === "";
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose,
      maxWidth: "md",
      fullWidth: true,
      disableRestoreFocus: true,
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: "Devices" }),
        /* @__PURE__ */ jsx(DialogContent, { sx: { minHeight: 200 }, children: loading ? /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200
            },
            children: /* @__PURE__ */ jsx(CircularProgress, {})
          }
        ) : /* @__PURE__ */ jsxs(Fragment, { children: [
          errorMessage != null && /* @__PURE__ */ jsx(Typography, { color: "error", sx: { mb: 2 }, children: errorMessage }),
          devices.length === 0 && /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "body2",
              color: "text.disabled",
              sx: { textAlign: "center", py: 2 },
              children: "No devices yet."
            }
          ),
          isAdmin ? groupByUser(devices).map(([userId, userDevices]) => /* @__PURE__ */ jsxs(Box, { sx: { mb: 2 }, children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "overline",
                color: "text.secondary",
                sx: { display: "block", mb: 1 },
                children: userHeading(userId)
              }
            ),
            renderDeviceList(userDevices)
          ] }, userId)) : /* @__PURE__ */ jsxs(Box, { children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "overline",
                color: "text.secondary",
                sx: { display: "block", mb: 1 },
                children: "My Devices"
              }
            ),
            renderDeviceList(devices)
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(DialogActions, { children: /* @__PURE__ */ jsx(Button, { onClick: onClose, children: "Close" }) }),
        edit != null && /* @__PURE__ */ jsxs(Dialog, { open: true, onClose: closeEdit, maxWidth: "xs", fullWidth: true, children: [
          /* @__PURE__ */ jsx(DialogTitle, { children: edit.mode === "approve" ? "Approve device" : "Rename device" }),
          /* @__PURE__ */ jsxs(DialogContent, { children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "caption",
                color: "text.secondary",
                sx: { display: "block", fontFamily: "monospace", mb: 2 },
                children: edit.id
              }
            ),
            /* @__PURE__ */ jsx(
              TextField,
              {
                fullWidth: true,
                margin: "dense",
                required: true,
                autoFocus: true,
                label: "Device name",
                placeholder: "e.g. Alice's laptop",
                value: edit.name,
                error: editNameMissing,
                helperText: editNameMissing ? "Device name is required" : " ",
                onChange: (e) => {
                  setEdit({ ...edit, name: e.target.value });
                }
              }
            ),
            /* @__PURE__ */ jsx(
              TextField,
              {
                fullWidth: true,
                margin: "dense",
                label: "Description",
                placeholder: "Optional description",
                multiline: true,
                minRows: 2,
                value: edit.description,
                onChange: (e) => {
                  setEdit({ ...edit, description: e.target.value });
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(DialogActions, { children: [
            /* @__PURE__ */ jsx(Button, { disabled: editBusy, onClick: closeEdit, children: "Cancel" }),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "contained",
                disabled: editBusy || editNameMissing,
                onClick: () => void handleSaveEdit(),
                children: edit.mode === "approve" ? "Approve" : "Save"
              }
            )
          ] })
        ] })
      ]
    }
  );
};

// lib/device/DeviceUnauthorizedDialog.tsx
import {
  Button as Button2,
  Dialog as Dialog2,
  DialogActions as DialogActions2,
  DialogContent as DialogContent2,
  DialogTitle as DialogTitle2,
  Typography as Typography2
} from "@mui/material";
import { useSyncExternalStore } from "react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var DeviceUnauthorizedDialog = () => {
  const state = useSyncExternalStore(
    subscribeDeviceUnauthorized,
    getDeviceUnauthorizedSnapshot
  );
  return /* @__PURE__ */ jsxs2(
    Dialog2,
    {
      open: state.blocked,
      onClose: clearDeviceUnauthorized,
      maxWidth: "sm",
      fullWidth: true,
      children: [
        /* @__PURE__ */ jsx2(DialogTitle2, { children: "Device authorization required" }),
        /* @__PURE__ */ jsxs2(DialogContent2, { children: [
          /* @__PURE__ */ jsx2(Typography2, { sx: { mb: 2 }, children: "This device isn't approved yet. Log in from a machine that already has access, then go to your user icon > Devices and approve this device." }),
          state.deviceId != null && /* @__PURE__ */ jsxs2(Typography2, { variant: "body2", color: "text.secondary", children: [
            "Device ID: ",
            /* @__PURE__ */ jsx2("code", { children: state.deviceId })
          ] })
        ] }),
        /* @__PURE__ */ jsx2(DialogActions2, { children: /* @__PURE__ */ jsx2(Button2, { onClick: clearDeviceUnauthorized, children: "Dismiss" }) })
      ]
    }
  );
};

// lib/device/process-env.ts
if (typeof globalThis.process === "undefined") {
  ;
  globalThis.process = { env: {} };
}

// lib/device/env.ts
var deviceEnforcementMode = () => {
  const raw = process.env.AUTH_DEVICE;
  switch (raw) {
    case "enforce":
    case "learning":
    case "permissive":
      return raw;
    default:
      return "disabled";
  }
};
var isDeviceAuthEnabled = () => deviceEnforcementMode() !== "disabled";

export {
  devicesBaseUrlForTesting,
  listDevices,
  updateDevice,
  removeDevice,
  DevicesDialog,
  DeviceUnauthorizedDialog,
  deviceEnforcementMode,
  isDeviceAuthEnabled
};
